import { z } from 'zod';
import { uuidSchema } from './common';

/**
 * 카카오톡 채널 주소 — `https://pf.kakao.com/_채널ID`(채널 홈) 또는 그 뒤 `/chat`(1:1 채팅). 채널 ID 는 `_` 로 시작하는 영문·숫자다.
 * 호스트를 고정해 관리자 화면으로 고객 사이트에 다른 링크를 심지 못하게 한다(운영 설정 설계 K4).
 */
export const KAKAO_CHANNEL_URL_PATTERN =
  /^https:\/\/pf\.kakao\.com\/(_[A-Za-z0-9]+)(?:\/chat)?\/?$/;
export const KAKAO_CHANNEL_URL_MESSAGE =
  '카카오톡 채널 주소는 https://pf.kakao.com/_채널ID 형식이어야 합니다.';
export const KAKAO_CHANNEL_URL_MAX_LENGTH = 200;

export const kakaoChannelUrlSchema = z
  .string()
  .trim()
  .max(KAKAO_CHANNEL_URL_MAX_LENGTH, KAKAO_CHANNEL_URL_MESSAGE)
  .regex(KAKAO_CHANNEL_URL_PATTERN, KAKAO_CHANNEL_URL_MESSAGE);

/** 채널 홈·채팅 주소 어느 쪽을 넣어도 1:1 채팅 주소로 바꾼다. 형식이 맞지 않으면 그대로 돌려준다(저장 전 계약이 거른다). */
export const kakaoChannelChatUrlOf = (url: string): string => {
  const channelId = KAKAO_CHANNEL_URL_PATTERN.exec(url.trim())?.[1];
  return channelId === undefined ? url : `https://pf.kakao.com/${channelId}/chat`;
};

/** 고객용 웹 공개 응답(siteSettings.getPublic) — 버튼에 쓸 채팅 주소 하나. 비어 있으면 버튼을 그리지 않는다(K3). */
export const siteSettingsPublicSchema = z.strictObject({
  kakaoChannelChatUrl: z.string().nullable(),
});
export type SiteSettingsPublic = z.infer<typeof siteSettingsPublicSchema>;

/** 관리자 '운영 설정' 저장 입력 — 주소를 비우면(null) 버튼이 사라진다. 변경자·시각은 서버가 정한다. */
export const updateSiteSettingsInputSchema = z.strictObject({
  kakaoChannelUrl: kakaoChannelUrlSchema.nullable(),
});
export type UpdateSiteSettingsInput = z.infer<typeof updateSiteSettingsInputSchema>;

/** 관리자 '운영 설정' 조회·저장 결과 — 변경자 이름은 화면 표시용. 행이 없으면 변경 기록이 null 이다. */
export const adminSiteSettingsSchema = z.strictObject({
  kakaoChannelUrl: z.string().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  updatedBy: z.strictObject({ id: uuidSchema, name: z.string() }).nullable(),
});
export type AdminSiteSettings = z.infer<typeof adminSiteSettingsSchema>;
